const WEEKDAYS: Record<string, string[]> = {
  de: ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"],
  en: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
};
const MONTHS: Record<string, string[]> = {
  de: ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"],
  en: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
};

/**
 * Compact co-organizer label, mirrors the platform (#99):
 *   0 → "A", 1 → "A × B", 2+ → "A × {n} weitere".
 * Pure so it can be unit-tested without rendering.
 */
export function compactCoOrgLabel(
  mainName: string,
  coOrganizers: { name: string }[] | undefined,
  moreLabel: (count: number) => string,
): string {
  const co = coOrganizers ?? [];
  if (co.length === 0) return mainName;
  if (co.length === 1) return `${mainName} × ${co[0].name}`;
  return `${mainName} × ${moreLabel(co.length)}`;
}

// All cleanup times are Berlin wall-clock. Resolving against a fixed timezone
// (not the viewer's runtime one) keeps the embed's times identical to the
// platform for every viewer: a widget on a third-party site is read from
// anywhere, so getHours()/getDate() (local time) would show the wrong time for
// a non-Berlin viewer. Mirrors frontend/src/lib/format.ts.
const BERLIN_TZ = "Europe/Berlin";

interface BerlinParts {
  year: number;
  month: number;
  day: number;
  hour: string;
  minute: string;
  weekdayIndex: number;
}

function berlinParts(input: string | Date): BerlinParts {
  const d = typeof input === "string" ? new Date(input) : input;
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: BERLIN_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  const p: Record<string, string> = {};
  for (const part of fmt.formatToParts(d)) {
    if (part.type !== "literal") p[part.type] = part.value;
  }
  const year = Number(p.year);
  const month = Number(p.month);
  const day = Number(p.day);
  // Day-of-week from the Berlin calendar date, computed timezone-free via UTC.
  const weekdayIndex = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return { year, month, day, hour: p.hour, minute: p.minute, weekdayIndex };
}

export interface DateParts {
  weekday: string;
  day: string;
  month: string;
  time: string;
}

// locale-aware weekday/month abbreviations; time stays 24h in both locales.
// Resolved in the fixed Berlin timezone so it matches the platform everywhere.
export function dateParts(iso: string, locale = "de"): DateParts {
  const { day, month, hour, minute, weekdayIndex } = berlinParts(iso);
  const weekdays = WEEKDAYS[locale] ?? WEEKDAYS.de;
  const months = MONTHS[locale] ?? MONTHS.de;
  return {
    weekday: weekdays[weekdayIndex] ?? "",
    day: String(day).padStart(2, "0"),
    month: months[month - 1] ?? "",
    time: `${hour}:${minute}`,
  };
}

// "HH:MM" in Berlin wall-clock — used for the computed end time (start + duration).
export function berlinTime(input: string | Date): string {
  const { hour, minute } = berlinParts(input);
  return `${hour}:${minute}`;
}

// "DD.MM." in Berlin wall-clock — used by the map marker popup.
export function berlinShortDate(iso: string): string {
  const { day, month } = berlinParts(iso);
  return `${String(day).padStart(2, "0")}.${String(month).padStart(2, "0")}.`;
}
